import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { Subscription } from "rxjs/Subscription";
import { AuthService } from "../../app/auth/auth.service";
import {
  EvaChatResponse,
  EvaMessage,
  EvaRating,
  EvaService,
} from "../../providers/eva/eva.service";

@Component({
  selector: "eva-chat",
  templateUrl: "eva-chat.html",
})
export class EvaChatComponent implements OnInit, OnDestroy {
  isOpen = false;
  messages: EvaMessage[] = [];
  inputText = "";
  loading = false;
  errorMessage = "";

  /**
   * Drives the CO-3559 reopen tab. See EvaService.hasBeenOpened$.
   *
   * No separate platform gate is needed to keep the tab off mobile. The tab
   * only renders once the panel has been opened, and the sole caller of
   * EvaService.open() is the ellipsis menu entry in user-popover.html, which is
   * itself gated on !isMobilePlatform. So inside the native shell the panel is
   * never opened, this stays false, and the tab never appears. Asserted by the
   * "stays hidden until the panel has actually been opened" spec rather than
   * restated as a redundant condition here.
   */
  hasBeenOpened = false;

  // Starter questions are limited to topics the knowledge base actually covers.
  // "How do I submit a recertification?" was removed in CO-3559: the approved
  // corpus has no recertification submission page, only a worksheet and the CTI
  // page, so it reliably produced a thin or off-target answer.
  quickQuestions: string[] = [
    "How do I create a patient chart?",
    "How does the billing workflow work?",
    "How do I generate a correction claim?",
  ];

  @ViewChild("scrollAnchor") scrollAnchor: ElementRef;

  private subscriptions: Subscription[] = [];

  constructor(
    public eva: EvaService,
    private authSvc: AuthService,
    private hostEl: ElementRef
  ) {}

  ngOnInit() {
    // Portal the host element to document.body so position:fixed children
    // escape any transformed ancestors (Ionic 3 applies transform on
    // <ion-content> and <ion-nav>, which create CSS containing blocks).
    const host = this.hostEl.nativeElement as HTMLElement;
    if (host && host.parentElement !== document.body) {
      document.body.appendChild(host);
    }

    this.subscriptions.push(
      this.eva.isOpen$.subscribe((open: boolean) => {
        this.isOpen = open;
      })
    );

    this.subscriptions.push(
      this.eva.hasBeenOpened$.subscribe((opened: boolean) => {
        this.hasBeenOpened = opened;
      })
    );
  }

  close() {
    this.eva.close();
  }

  /**
   * CO-3559: the reopen tab's vertical position, in px from the top of the
   * viewport. null means "use the stylesheet default", which is the upper right.
   * Set only once the user drags the tab, and not persisted, so it resets on
   * reload like the rest of the panel's session state.
   */
  tabTopPx: number = null;

  private tabDragging = false;
  /** True when the pointer moved far enough to count as a drag, not a tap. */
  private tabDragged = false;
  private tabGrabOffset = 0;
  private tabDragStartY = 0;
  private tabHeight = 0;
  private tabMoveHandler: any = null;
  private tabEndHandler: any = null;

  /** Pixels of movement before a press counts as a drag rather than a tap. */
  private static readonly DRAG_THRESHOLD_PX = 4;
  /** Keeps the tab off the very top and bottom edges. */
  private static readonly DRAG_EDGE_MARGIN_PX = 8;

  /** CO-3559: one click back into the panel from the right edge tab. */
  reopen(event?: any) {
    // A drag ends in a click on the same element, so without this the tab would
    // open the panel every time the user repositioned it.
    //
    // A keyboard activation is exempt. UIEvent.detail is the click count, which
    // the spec fixes at 0 for Enter and Space and at 1 or more for a real
    // pointer click, so it separates the two without a timer. Without the
    // exemption a drag that ends off the tab leaves tabDragged set with no
    // pointer click coming to consume it, and the next Enter is swallowed
    // instead of opening the panel.
    const fromKeyboard = !!event && event.detail === 0;
    if (this.tabDragged && !fromKeyboard) {
      this.tabDragged = false;
      return;
    }
    this.tabDragged = false;
    this.eva.open();
  }

  /**
   * Start dragging the tab up or down its edge. Bound to both mousedown and
   * touchstart because the deployment target is iPad Safari but the app is also
   * used on desktop.
   *
   * The move and end listeners go on document, not the button, so the drag
   * survives the pointer leaving a 42px-wide target. Zone.js patches
   * addEventListener, so these still run inside Angular and the [style.top]
   * binding updates without an explicit detectChanges.
   */
  onTabDragStart(event: any): void {
    const point = this.pointerOf(event);
    if (!point) {
      return;
    }
    // Drop any set left behind by a press that never resolved, for instance a
    // button released outside the window so no mouseup ever reached us. The
    // handler fields below are overwritten either way, and removeTabDragListeners
    // can only ever unbind whatever they point at, so without this the earlier
    // pair stays on document for the life of the page.
    this.removeTabDragListeners();
    const box = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.tabDragging = true;
    this.tabDragged = false;
    this.tabGrabOffset = point.clientY - box.top;
    this.tabDragStartY = point.clientY;
    this.tabHeight = box.height;

    this.tabMoveHandler = (e: any) => this.onTabDragMove(e);
    this.tabEndHandler = () => this.onTabDragEnd();
    document.addEventListener("mousemove", this.tabMoveHandler);
    document.addEventListener("mouseup", this.tabEndHandler);
    document.addEventListener("touchmove", this.tabMoveHandler);
    document.addEventListener("touchend", this.tabEndHandler);
    document.addEventListener("touchcancel", this.tabEndHandler);
  }

  private onTabDragMove(event: any): void {
    if (!this.tabDragging) {
      return;
    }
    const point = this.pointerOf(event);
    if (!point) {
      return;
    }
    if (
      Math.abs(point.clientY - this.tabDragStartY) >=
      EvaChatComponent.DRAG_THRESHOLD_PX
    ) {
      this.tabDragged = true;
    }
    // Stop the page scrolling under a touch drag.
    if (event.cancelable) {
      event.preventDefault();
    }
    this.tabTopPx = this.clampTabTop(point.clientY - this.tabGrabOffset);
  }

  private onTabDragEnd(): void {
    this.tabDragging = false;
    this.removeTabDragListeners();
  }

  private removeTabDragListeners(): void {
    if (!this.tabMoveHandler) {
      return;
    }
    document.removeEventListener("mousemove", this.tabMoveHandler);
    document.removeEventListener("mouseup", this.tabEndHandler);
    document.removeEventListener("touchmove", this.tabMoveHandler);
    document.removeEventListener("touchend", this.tabEndHandler);
    document.removeEventListener("touchcancel", this.tabEndHandler);
    this.tabMoveHandler = null;
    this.tabEndHandler = null;
  }

  /**
   * A press that ends outside the window never delivers mouseup, so the drag
   * would otherwise stay armed and the tab would keep tracking the pointer once
   * it came back with no button held.
   */
  @HostListener("window:blur")
  onWindowBlur(): void {
    if (this.tabDragging) {
      this.onTabDragEnd();
    }
  }

  /**
   * Re-clamp a dragged tab against the new viewport (CO-3559).
   *
   * The stylesheet default is a percentage and rescales by itself, but a drag
   * replaces it with an inline pixel value, and clampTabTop only ever ran from
   * the move handler. So a tab parked low in iPad portrait sat below the bottom
   * edge after a rotation to landscape, with no way back: the percentage default
   * only applies while tabTopPx is null, and reloading clears hasBeenOpened and
   * takes the tab away entirely.
   *
   * resize rather than orientationchange, deliberately. It is the signal that
   * the viewport actually changed, which is all the clamp depends on, and it is
   * the only one of the two with any precedent in this app. orientationchange
   * can also fire before innerHeight reflects the new orientation, and does not
   * fire at all when an iPad multitasking split resizes the app without rotating
   * the device. Undebounced, matching the sibling handlers, so the tab tracks a
   * live desktop resize instead of overshooting and snapping back.
   */
  @HostListener("window:resize")
  onWindowResize(): void {
    if (this.tabTopPx !== null) {
      this.tabTopPx = this.clampTabTop(this.tabTopPx);
    }
  }

  /** Keeps the tab fully on screen, whatever the viewport or the drag. */
  clampTabTop(top: number): number {
    const margin = EvaChatComponent.DRAG_EDGE_MARGIN_PX;
    const height = this.tabHeight || 54;
    const max = Math.max(margin, window.innerHeight - height - margin);
    return Math.min(Math.max(top, margin), max);
  }

  private pointerOf(event: any): { clientY: number } {
    if (event && event.touches && event.touches.length) {
      return event.touches[0];
    }
    if (event && event.changedTouches && event.changedTouches.length) {
      return event.changedTouches[0];
    }
    return event && typeof event.clientY === "number" ? event : null;
  }

  /**
   * True while any Ionic overlay is on screen, so the reopen tab can get out of
   * the way.
   *
   * This exists because the tab CANNOT be pushed under Ionic's overlays with a
   * z-index. <ion-app> is `position: absolute; z-index: 0`, so it is a stacking
   * context, and this component's host is portaled to document.body in ngOnInit.
   * The tab is therefore a sibling of <ion-app>, and any positive z-index on it
   * paints above everything inside <ion-app>, including a popover measured at
   * z-index 10001. Verified in a browser: with a popover open, the tab stayed
   * visible and document.elementFromPoint at its centre returned the tab's own
   * icon, so it was also still clickable. Lowering the number does not help,
   * because even z-index auto paints later than <ion-app> in DOM order.
   *
   * Read from the DOM rather than tracked in state because these overlays are
   * opened by unrelated pages all over the app, and there is no shared service
   * this component could subscribe to. The query runs on change detection, which
   * Ionic triggers when an overlay opens or closes.
   *
   * Every selector here was taken from the installed library rather than guessed,
   * because THE TAG DOES NOT ALWAYS MATCH THE COMPONENT NAME. The picker is
   * `ion-picker-cmp`, not `ion-picker` (see
   * node_modules/ionic-angular/components/picker/picker-component.js, `selector`).
   * That one matters most: `ion-datetime` presents a picker, and date fields are
   * on every order form in the app, so an `ion-picker` selector would have left
   * the tab floating over the single most common overlay in the product.
   *
   * `.sync-button-overlay` is not an Ionic overlay at all. It is this app's own
   * full-screen click blocker for the offline sync warning (app.html:20,
   * app.scss:459). It lives INSIDE <ion-app>, so its z-index of 1000001 is
   * trapped in that stacking context and cannot cover this tab either.
   *
   * `ion-toast` is deliberately NOT in the list. A toast is anchored to the top or
   * bottom edge and never occludes the tab at its default 22% height, and this
   * app raises toasts with no duration and a close button (see errorResponse.ts),
   * so treating one as a blocking overlay would remove the tab until the user
   * dismissed it.
   */
  get overlayOpen(): boolean {
    return !!document.querySelector(
      "ion-popover, ion-modal, ion-alert, ion-loading, ion-action-sheet, ion-picker-cmp, .sync-button-overlay"
    );
  }

  resetChat() {
    if (this.loading) {
      return;
    }
    this.messages = [];
    this.inputText = "";
    this.errorMessage = "";
  }

  trackByIdx(index: number): number {
    return index;
  }

  sendMessage() {
    const text = (this.inputText || "").trim();
    if (!text || this.loading) {
      return;
    }

    // Snapshot the history BEFORE the pending message joins this.messages:
    // the question being asked travels as the request's `message` field, so
    // including it here too would send it twice and leave history odd-length.
    const history = this.messages.slice(-12);

    this.messages.push({ role: "user", content: text });
    this.inputText = "";
    this.loading = true;
    this.errorMessage = "";
    this.scrollToBottom();

    this.subscriptions.push(
      this.eva.chat(text, history).subscribe(
        (resp: EvaChatResponse) => {
          this.loading = false;
          this.handleResponse(resp);
          this.scrollToBottom();
        },
        (err: any) => {
          this.loading = false;
          this.errorMessage =
            "Eva is temporarily unavailable. Please try again in a moment.";
        }
      )
    );
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  askQuickQuestion(q: string) {
    if (this.loading) {
      return;
    }
    this.inputText = q;
    this.sendMessage();
  }

  /**
   * Render the small subset of Markdown that the agent produces.
   * Order matters: HTML-escape FIRST (XSS guard), then transform.
   * Supports: **bold**, *italic*, `code`, [text](url), bare email addresses,
   * bullets (- or *), numbered lists (1. text), blank-line paragraphs,
   * single-newline breaks.
   */
  /**
   * Renders a chat message for display. Assistant messages go through the
   * markdown formatter (which escapes HTML first); user messages are only
   * HTML-escaped, never formatted - they are plain text the user typed and
   * must never be interpreted as live markup (XSS guard).
   */
  renderMessage(msg: EvaMessage): string {
    if (!msg || !msg.content) {
      return "";
    }
    return msg.role === "assistant"
      ? this.renderMarkdown(msg.content)
      : this.escapeHtml(msg.content);
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  renderMarkdown(text: string): string {
    if (!text) {
      return "";
    }
    // 1. HTML-escape
    let html = this.escapeHtml(text);

    // 2. Code spans (do BEFORE bold/italic to avoid * inside code being processed)
    html = html.replace(/`([^`\n]+?)`/g, "<code>$1</code>");

    // 3. Links [text](url)
    html = html.replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );

    // 3b. Bare email addresses -> mailto links (CO-3525).
    //
    // Every support-routed sentence the backend serves names Curantis Support
    // by address in plain prose, so the address arrives as text and, rendered
    // as text, it is not clickable. This is a render rule, not a copy rule: it
    // fires on the SHAPE of an address in any assistant message, so no backend
    // path has to opt in and no new sentence can forget to.
    html = this.linkifyEmails(html);

    // 4. Bold **text** (must run BEFORE italic - both use *)
    html = html.replace(/\*\*([^*\n]+?)\*\*/g, "<strong>$1</strong>");

    // 5. Italic *text* (single asterisk, no leading space inside)
    html = html.replace(/(^|[^*])\*(?!\s)([^*\n]+?)\*(?!\*)/g, "$1<em>$2</em>");

    // 6. Numbered list lines: "1. text" -> wrapped, keep numbering visible
    // We're not building <ol>, just preserving readability.
    // 7. Bullet list lines: "- text" or "* text" at line start -> "• text"
    html = html.replace(/^[\-\*]\s+/gm, "• ");

    // 8. Paragraph + line breaks
    //    Double newline -> paragraph break; single newline -> <br>
    html = html.replace(/\n{2,}/g, "</p><p>");
    html = html.replace(/\n/g, "<br>");
    html = "<p>" + html + "</p>";

    return html;
  }

  /**
   * Wraps every bare email address in a mailto link, rewriting PLAIN TEXT and
   * nothing else (CO-3525).
   *
   * This runs after steps 2 and 3, so by now the string is part prose and part
   * markup those steps generated, and an address can sit inside that markup:
   * as a query parameter of a linked URL, or in a link's own text. Rewriting
   * either one corrupts the href or nests an <a> inside an <a>. So the string
   * is split on the generated markup FIRST and the rule is applied only to the
   * prose between. String.split with a capturing group alternates prose (even
   * slices) and the captured markup (odd slices), which is what makes that
   * partition exact: escapeHtml() ran before step 2, so after it a `<` can
   * only be markup one of those steps produced, never content.
   *
   * The leading character is still CAPTURED rather than looked behind (the ES5
   * target has no lookbehind), as the italic rule does. Its job is now only to
   * keep the address from being torn out of a longer plain-text token - the
   * excluded characters are the ones a URL is built from, so a bare unlinked
   * URL that carries an address stays intact. No target/rel on the anchor: a
   * mail client should not be opened as a browser tab.
   */
  private linkifyEmails(html: string): string {
    return html
      .split(/(<a\b[^>]*>[\s\S]*?<\/a>|<code>[\s\S]*?<\/code>)/)
      .map((slice, i) =>
        i % 2 === 1
          ? slice
          : slice.replace(
              /(^|[^A-Za-z0-9._%+\-\/=?#&:])([A-Za-z0-9._%+\-]+@[A-Za-z0-9\-]+(?:\.[A-Za-z0-9\-]+)*\.[A-Za-z]{2,})/g,
              '$1<a href="mailto:$2">$2</a>'
            )
      )
      .join("");
  }

  /**
   * Records a thumbs up or thumbs down for one answer (CO-3529).
   *
   * The selection is set BEFORE the call and put back if the call fails, so
   * what the thumb shows is only ever what the server was told to store. The
   * alternative, leaving the thumb lit on a failure, would tell the clinician
   * their rating was recorded when it was not, and it is an unfalsifiable lie:
   * the same button now looks done, so nobody retries. Reverting is its own
   * feedback - the tap visibly does not stick - and the button stays live.
   *
   * Only ONE request per answer is in the air at a time. A tap made while one is
   * out changes what the thumb shows and stops there; the value goes on the wire
   * when the outstanding request settles. Two requests at once would leave the
   * stored value to be decided by which response ARRIVED last, since nothing in
   * the body carries a sequence, and would let a request a later tap has already
   * superseded write the panel back. Sending them one after the other is what
   * makes tap order decide, and no queue is needed to remember the waiting tap:
   * `msg.rating` already holds the latest one, and only the latest is worth
   * sending.
   *
   * Nothing else moves. `messages` is not appended to, `errorMessage` stays
   * empty and `loading` is untouched, because a rating is an optional aside and
   * a failed one must not put a banner in the conversation or block the
   * composer. Mutating the message in place rather than replacing it is what
   * keeps a rejected call out of the message list entirely. The error callback
   * is also the reason the rejection cannot surface as an unhandled one, which
   * covers the not-configured case too: rate() throws on subscribe there.
   *
   * A tap on the thumb already selected does nothing and sends nothing. The
   * vocabulary is up and down with no third value, so there is no un-rating to
   * express, and the state on screen is already the state stored.
   *
   * Not gated on `loading`: an earlier answer can be rated while a new question
   * is in flight, and a rating does not draw on the daily question allowance.
   */
  rateAnswer(msg: EvaMessage, rating: EvaRating) {
    if (!msg || !msg.answerId || msg.rating === rating) {
      return;
    }
    msg.rating = rating;
    if (msg.ratingInFlight) {
      return;
    }
    this.postRating(msg, rating);
  }

  private postRating(msg: EvaMessage, rating: EvaRating) {
    msg.ratingInFlight = rating;
    this.subscriptions.push(
      this.eva.rate(msg.answerId, rating).subscribe(
        () => {
          this.settleRating(msg, rating, true);
        },
        () => {
          this.settleRating(msg, rating, false);
        }
      )
    );
  }

  /**
   * Closes out the rating request that carried `sent`, `stored` saying whether
   * the server took it (CO-3529).
   *
   * `sent` is what makes this safe to run late: it is compared with the thumb on
   * screen, so a request the clinician has already superseded knows that it has
   * been, and reverts nothing. Only the request whose value is still the one
   * shown may put the thumb back, and it goes back to what the server confirmed,
   * not to a value captured at tap time.
   *
   * When the thumb has moved on, that newer value is what goes out next - unless
   * the server already holds it, which happens when the taps went back to where
   * they started. Then there is nothing to tell it.
   */
  private settleRating(msg: EvaMessage, sent: EvaRating, stored: boolean) {
    msg.ratingInFlight = undefined;
    if (stored) {
      msg.ratingStored = sent;
    }
    const latest = msg.rating;
    if (latest === sent) {
      if (!stored) {
        msg.rating = msg.ratingStored;
      }
      return;
    }
    if (latest && latest !== msg.ratingStored) {
      this.postRating(msg, latest);
    }
  }

  createTicket(message: EvaMessage) {
    if (this.loading) {
      return;
    }
    // Use the most recent USER question as the ticket summary.
    let userQuestion = "Eva escalation";
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].role === "user") {
        userQuestion = this.messages[i].content;
        break;
      }
    }
    const summary =
      userQuestion.length > 80
        ? userQuestion.substring(0, 77) + "..."
        : userQuestion;
    const transcript = this.messages
      .map((m) => `${m.role.toUpperCase()}: ${m.content}`)
      .join("\n\n");

    let userName = "";
    let userEmail = "";
    try {
      const user = this.authSvc.getUser();
      if (user) {
        userName = (user as any).firstName
          ? `${(user as any).firstName} ${(user as any).lastName || ""}`.trim()
          : (user as any).username || "";
        userEmail = (user as any).email || "";
      }
    } catch (e) {
      // user info not available - fine, just send blanks
    }

    this.loading = true;
    this.subscriptions.push(
      this.eva.createTicket(summary, transcript, userName, userEmail).subscribe(
        (resp) => {
          this.loading = false;
          this.messages.push({
            role: "assistant",
            content: `Support ticket ${resp.ticket_key} created. The support team will follow up.`,
            ticketKey: resp.ticket_key,
            ticketUrl: resp.ticket_url,
          });
          this.scrollToBottom();
        },
        (err: any) => {
          this.loading = false;
          this.errorMessage =
            "Failed to create the support ticket. Please try again or contact support directly.";
        }
      )
    );
  }

  private handleResponse(resp: EvaChatResponse) {
    let content = "";
    switch (resp.action) {
      case "ANSWER":
        content = resp.answer || "(no answer)";
        break;
      case "REDIRECT":
        content =
          (resp.message || "") +
          (resp.page_title && resp.page_url
            ? `\n\nSee: [${resp.page_title}](${resp.page_url})`
            : "");
        break;
      case "ASK_FOLLOWUP":
        content = resp.question || "(no question)";
        break;
      case "CREATE_TICKET":
        content =
          resp.reason ||
          "I couldn't find an answer to that. Would you like me to open a support ticket?";
        break;
      default:
        content = resp.answer || "(no response)";
    }
    this.messages.push({
      role: "assistant",
      content: content,
      sources: resp.sources || [],
      action: resp.action,
      // CO-3529: carried onto the message, not held on the component, so a
      // rating reaches the answer it was given for while earlier answers are
      // still on screen. Every branch above lands here, so abstentions,
      // refusals, deflections and a spent quota are all rateable - a thumbs
      // down on a bad abstention is the signal this feature exists for.
      answerId: resp.answer_id,
    });
  }

  private scrollToBottom() {
    // Set scrollTop = scrollHeight directly on the messages container.
    // (scrollIntoView wasn't working because #scrollAnchor was on the container
    // itself, not on a sentinel at the bottom.) Defer to next tick so DOM
    // updates first.
    setTimeout(() => {
      const el = this.scrollAnchor && this.scrollAnchor.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 50);
  }

  ngOnDestroy() {
    this.removeTabDragListeners();
    this.subscriptions.forEach((s) => s.unsubscribe());
    const host = this.hostEl.nativeElement as HTMLElement;
    if (host && host.parentElement === document.body) {
      document.body.removeChild(host);
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/eva-chat/eva-chat.ts